'use client';

import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCash,
  IconLogin,
  IconPlus,
  IconShoppingBag,
  IconShoppingCart,
} from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { authAtom } from '@website/atoms';
import { QuantityInput } from '@website/components/quantity-input';
import type { CartItem, ProductListItem } from '@website/types';
import { api, handleTreatyResponse } from '@website/utils/api';
import { useAtomValue } from 'jotai/react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';

export function CartAddButton({
  product,
  productInCart,
}: {
  product: ProductListItem;
  productInCart?: CartItem;
}) {
  const router = useRouter();
  const t = useTranslations();
  const auth = useAtomValue(authAtom);
  const [opened, { close, open }] = useDisclosure();

  const form = useForm({
    initialValues: {
      quantity: 1,
    },
  });

  const addMutation = useMutation({
    onSuccess: () => {
      close();
      router.refresh();
    },
    mutationFn: async (quantity: number) => {
      const res = await api.cart.add.post(
        {
          quantity,
          productId: product.id,
          includeInstallation: false,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        },
      );

      return handleTreatyResponse(res);
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    await addMutation.mutateAsync(values.quantity);
  });

  const totalPriceFormatted = useMemo(() => {
    const productPrice = product.price;
    const quantity = form.getValues().quantity;
    const totalPrice = productPrice * quantity;
    return `${productPrice.toLocaleString()} x ${quantity.toLocaleString()} = ${totalPrice.toLocaleString()} ${t('iqd')}`;
  }, [product.price, form, t]);

  return (
    <Box>
      <Modal opened={opened} onClose={close} withCloseButton={false}>
        <form onSubmit={handleSubmit}>
          {auth.status === 'Loading' ? (
            <Stack align="center">
              <Loader />

              <Text>{t('login.checkingAuth')}</Text>
            </Stack>
          ) : auth.status === 'Authenticated' ? (
            <Stack>
              <Center>
                <QuantityInput {...form.getInputProps('quantity')} />
              </Center>

              <Divider />

              <Alert dir="ltr" icon={<IconCash />}>
                {totalPriceFormatted}
              </Alert>

              <Divider />

              <Button
                type="submit"
                loading={form.submitting}
                leftSection={<IconShoppingBag />}
              >
                {t('carts.addToCart')}
              </Button>
            </Stack>
          ) : (
            <Stack>
              <Text>{t('login.loginFirst')}</Text>
              <Button
                href="/login"
                variant="light"
                component={Link}
                leftSection={<IconLogin />}
              >
                {t('login.login')}
              </Button>
            </Stack>
          )}
        </form>
      </Modal>

      {productInCart ? (
        <Stack gap="xs">
          <Text>
            {t('carts.itemAddedToCart', {
              quantity: productInCart.quantity,
            })}
          </Text>

          <Button
            fullWidth
            color="teal"
            href="/cart"
            component={Link}
            leftSection={<IconShoppingCart />}
          >
            {t('carts.goToCart')}
          </Button>
        </Stack>
      ) : (
        <Button fullWidth leftSection={<IconPlus />} onClick={open}>
          {t('carts.addToCart')}
        </Button>
      )}
    </Box>
  );
}
